import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { teamNormalisedAggregatesUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { hasEmptyInFilters } from '@/utils/api';
import { unwrapWithLoading } from '@/utils/jotai';
import { objSnakeToCamel } from '@/utils/queries';
import { fetchClientAtom } from '../client';
import { radarDataAtom } from '../radar';
import { teamRadarTableColumnsAtom } from '../tableColumns';
import { allTeamsOptionsAtom } from '../teams';
import { teamRadarTemplatesAtom } from '../userConfigs';
import { teamMetricsAtom } from './teamRadar';

export const comparisonTeamRadarFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
export const { debouncedValueAtom: comparisonTeamRadarFilterAtom } = comparisonTeamRadarFilterDebounceObject;

const rawComparisonTeamRadarDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const comparisonId = get(comparisonIdAtom);
  const filterParams = get(comparisonTeamRadarFilterAtom);

  const queryKey = ['comparisonTeamRadarData', comparisonId, filterParams] as const;

  const queryFn = async ({ queryKey: [, comparisonId, filterParams] }: { queryKey: typeof queryKey }) => {
    const url = teamNormalisedAggregatesUrl(comparisonId, filterParams);
    if (!url || hasEmptyInFilters(filterParams)) return {} as Record<string, number>;

    return (await fetch(url)) as Record<string, number>;
  };

  return { queryKey, queryFn };
});

export const comparisonTeamRadarDataAtom = unwrapWithLoading(
  radarDataAtom(rawComparisonTeamRadarDataAtom, teamMetricsAtom, teamRadarTemplatesAtom, teamRadarTableColumnsAtom),
  [],
);

export const comparisonTeamIsLoadingAtom = atom(get => {
  const comparisonId = get(comparisonIdAtom);
  if (!comparisonId) return false;

  const { isLoading: radarIsLoading } = get(comparisonTeamRadarDataAtom);
  return radarIsLoading;
});

const comparisonTeamNameAtom = atom(async get => {
  const comparisonId = get(comparisonIdAtom);
  if (!comparisonId) return '';
  const options = await get(allTeamsOptionsAtom);
  return options.find(opt => opt.value === comparisonId)?.label || '';
});
export const unwrappedComparisonTeamNameAtom = unwrap(comparisonTeamNameAtom, prev => prev || '');

const comparisonTeamAggDataAtom = atom(async get => {
  const data = objSnakeToCamel((await get(rawComparisonTeamRadarDataAtom)).data) as Record<string, number>;
  return {
    minutesPlayed: typeof data.minutesPlayed === 'number' ? data.minutesPlayed : 0,
    minutesPlayed360: typeof data.minutesPlayed360 === 'number' ? data.minutesPlayed360 : 0,
  };
});

const comparisonTeamMinutesPlayedAtom = atom(async get => (await get(comparisonTeamAggDataAtom)).minutesPlayed);
export const unwrappedComparisonTeamMinutesPlayedAtom = unwrap(comparisonTeamMinutesPlayedAtom, prev => prev ?? 0);

const comparisonTeamMinutesPlayed360Atom = atom(async get => (await get(comparisonTeamAggDataAtom)).minutesPlayed360);
export const unwrappedComparisonTeamMinutesPlayed360Atom = unwrap(
  comparisonTeamMinutesPlayed360Atom,
  prev => prev ?? 0,
);
